import React, { useState } from "react";
import "./ConsentBanner.css";
import { ConsentChoice, consentChoice, consentRequired, recordConsent } from "./consentState.ts";
import { useTranslation } from "../i18n/useTranslation.ts";

/**
 * The durable place to change an analytics answer. ADR 0025 §5 promised the
 * policy would say how, and amendment 2 §G makes it a control rather than a
 * sentence - once the banner is dismissed it is gone, so the policy page is
 * the only page that can carry this.
 *
 * Shown only where consent applies, which keeps §E's claim about non-European
 * visitors literally true: nothing is stored for them, so there is nothing
 * here for them to revoke. If that ever feels unfriendly - a US visitor who
 * would like to opt out and finds no control - the fix is a deliberate
 * decision to store a choice for people who were never asked, not an
 * accidental one.
 *
 * Revoking does not stop a tag that has already loaded this session; it stops
 * the next one. Saying otherwise would need a reload, and a policy page that
 * reloads itself when you touch a radio button is worse than the honest
 * version.
 */
export function ConsentSettings() {
    const t = useTranslation();
    const [required] = useState(() => consentRequired());
    const [choice, setChoice] = useState<ConsentChoice | null>(() => consentChoice());
    const [justSaved, setJustSaved] = useState(false);

    if (!required) {
        return null;
    }

    const answer = (next: ConsentChoice) => {
        recordConsent(next);
        setChoice(next);
        setJustSaved(true);
    };

    const status =
        choice === "granted"
            ? t("consent.statusGranted")
            : choice === "denied"
              ? t("consent.statusDenied")
              : t("consent.statusUndecided");

    return (
        <section className="consentSettings">
            <h2>{t("consent.settingsHeading")}</h2>
            <p className="consentSettingsStatus">
                {status} {justSaved && <strong>{t("consent.saved")}</strong>}
            </p>
            <div className="consentActions">
                <button type="button" className="consentButton" onClick={() => answer("denied")}>
                    {t("consent.reject")}
                </button>
                <button type="button" className="consentButton" onClick={() => answer("granted")}>
                    {t("consent.accept")}
                </button>
            </div>
        </section>
    );
}

export default ConsentSettings;
